<template>
  <div>
    <div class="d-flex mb-3 align-items-end">
      <h1 class="h4 primary--text">
        {{ $t('views.log_list.log_list') }}
      </h1>
      <div class="ml-auto">
        <v-btn
          outlined
          large
          @click="toggleFilter"
        >
          <v-icon left>
            mdi-filter-variant
          </v-icon>
          {{ $t('common.filter') }}
        </v-btn>
      </div>
    </div>
    <logs-filter
      v-model="isFilterShown"
      :filter="filter"
      @applyFilter="applyFilter"
    />
    <v-data-table
      fixed-header
      :headers="headers"
      :items="items"
      :footer-props="{
        itemsPerPageOptions: [10, 20, 50, 100],
      }"
      :options.sync="options"
      :server-items-length="total"
      :loading="loading"
      :loading-text="$t('common.loading_data')"
    >
      <template v-slot:item.actions="{ item }">
        <v-hover v-slot:default="{ hover }">
          <v-icon
            class="mr-4"
            :class="hover ? '' : 'text--disabled'"
            color="primary"
            size="20"
            @click="showDetailInfoModal(item, 'data')"
          >
            mdi-information-outline
          </v-icon>
        </v-hover>
        <v-hover v-slot:default="{ hover }">
          <v-icon
            class="mr-4"
            :class="hover ? '' : 'text--disabled'"
            color="primary"
            size="20"
            :hint="$t('views.log_list.service')"
            @click="showDetailInfoModal(item, 'ctx')"
          >
            mdi-text-account
          </v-icon>
        </v-hover>
      </template>
    </v-data-table>
    <detail-info-modal
      v-model="isDetailInfoModalShown"
      :detail-info="detailInfo"
    />
  </div>
</template>

<script>
import DetailInfoModal from '@/components/logs/DetailInfoModal.vue';
import LogsFilter from '@/components/logs/LogsFilter.vue';
import { mongoListRegex } from '@/utils/mongo-regex';

export default {
  name: 'LogList',

  components: {
    DetailInfoModal,
    LogsFilter,
  },

  data() {
    return {
      isDetailInfoModalShown: false,
      detailInfo: null,

      total: 0,
      items: [],
      loading: false,
      options: {
        sortBy: ['createdAt'],
        sortDesc: [true],
        page: 1,
      },

      headers: [
        {
          text: this.$t('common.date'),
          value: 'createdAt',
        },
        {
          text: this.$t('views.log_list.severity'),
          value: 'severity',
        },
        {
          text: this.$t('common.name'),
          value: 'name',
        },
        {
          text: this.$t('views.log_list.service'),
          value: 'ctx.service.name',
        },
        {
          text: this.$t('common.actions'),
          value: 'actions',
          sortable: false,
          width: 150,
          align: 'center',
        },
      ],

      filter: {
        createdAt: '',
        name: '',
        service: '',
        path: '',
        severity: '',
      },
      isFilterShown: false,
    };
  },

  watch: {
    options: {
      handler() {
        this.getItems();
      },
      deep: true,
    },
  },

  methods: {
    async getItems() {
      this.loading = true;

      const {
        sortBy, sortDesc, page, itemsPerPage,
      } = this.options;

      const params = {};
      params.query = this.mapFilter();
      params.pageSize = itemsPerPage;
      params.page = page;
      if (sortBy.length) {
        params.sort = `${sortDesc[0] ? '-' : ''}${sortBy[0]}`;
      }

      try {
        const { data = {} } = await this.$http.get('log-event', { params });

        this.items = (data.rows || []).map((it) => ({
          ...it,
          createdAt: it.createdAt && this.$moment(it.createdAt)
            .format('L LT'),
        }));
        this.total = data.total;
      } catch (err) {
        this.$http.mockApiError(err);
      } finally {
        this.loading = false;
      }
    },

    showDetailInfoModal(obj, prop) {
      this.isDetailInfoModalShown = true;
      this.detailInfo = (obj && obj[prop]) ? obj[prop] : null;
    },

    toggleFilter() {
      this.isFilterShown = !this.isFilterShown;
    },

    applyFilter(filter) {
      this.filter = { ...filter };
      this.options.page = 1;
      this.getItems();
    },

    mapFilter() {
      const filter = { $and: [] };
      if (this.filter.createdAt) {
        const [start, end] = this.filter.createdAt.replace(' to ', ' — ').split(' — ');
        filter.$and.push({
          createdAt: {
            $gte: this.$moment(start),
            $lte: this.$moment(end).endOf('day'),
          },
        });
      }
      if (this.filter.name) {
        filter.$and.push(mongoListRegex('name', this.filter.name, 'i'));
      }
      if (this.filter.service) {
        filter.$and.push(mongoListRegex('ctx.service.name', this.filter.service, 'i'));
      }
      if (this.filter.severity) {
        filter.$and.push({ severity: this.filter.severity });
      }

      let pathQuery = {};

      if (this.filter.path) {
        try {
          pathQuery = JSON.parse(this.filter.path);
          filter.$and.push(pathQuery);
        } catch (e) {
          pathQuery = {};
        }
      }

      if (filter.$and.length) { return filter; }

      return {};
    },
  },
};
</script>
